import {
  DELIVERY_WEEKDAYS,
  DELIVERY_WINDOWS_MAX,
  deliveryPlanningLabels,
  formatWeeklySchedule,
  weeklyScheduleSchema,
  type WeeklySchedule,
} from '@erp/shared';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InlineHelp } from '@/features/help';

// Incomplete rows must survive controlled updates until the surrounding form validates them.
const draftSchema = z.array(
  z.object({ weekdays: z.array(z.number()), from: z.string(), until: z.string() }).strict(),
);

function readDraft(value: string | null | undefined): WeeklySchedule | null {
  if (value === undefined || value === null || value === '') return [];
  try {
    const result = draftSchema.safeParse(JSON.parse(value));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export interface WeeklyScheduleEditorProps {
  id: string;
  label: string;
  value: string | null | undefined;
  onChange: (value: string | null) => void;
  error?: string | undefined;
  description?: string | undefined;
  disabled?: boolean;
  required?: boolean;
  previewLanguage?: string | undefined;
}

export function WeeklyScheduleEditor({
  id,
  label,
  value,
  onChange,
  error,
  description,
  disabled = false,
  required = false,
  previewLanguage,
}: WeeklyScheduleEditorProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const root = useRef<HTMLFieldSetElement>(null);
  const labels = deliveryPlanningLabels(i18n.language);
  const draft = readDraft(value);
  const windows = draft ?? [];
  const validation = weeklyScheduleSchema.safeParse(draft);
  const issues = validation.success ? [] : validation.error.issues;
  const messages = [
    ...new Set([
      ...(error === undefined
        ? []
        : [error.startsWith('customFields.weeklySchedule.') ? t(error) : error]),
      ...issues.map((issue) =>
        issue.message.startsWith('deliveryPlanning.')
          ? t(issue.message)
          : t('customFields.weeklySchedule.invalid'),
      ),
    ]),
  ];
  const describedBy = [
    `${id}-hint`,
    ...(description === undefined ? [] : [`${id}-description`]),
    ...(messages.length === 0 ? [] : [`${id}-error`]),
  ].join(' ');

  const focusFirstInvalid = useCallback(() => {
    const field =
      root.current?.querySelector<HTMLElement>('[aria-invalid="true"]:not(:disabled)') ??
      root.current?.querySelector<HTMLElement>('[data-weekly-schedule-add]:not(:disabled)') ??
      root.current?.querySelector<HTMLElement>('input:not(:disabled), button:not(:disabled)');
    field?.focus();
  }, []);

  useEffect(() => {
    if (error !== undefined && !disabled) focusFirstInvalid();
  }, [disabled, error, focusFirstInvalid]);

  const update = (next: WeeklySchedule): void => {
    // Save boundaries canonicalize; reordering here would move the row beneath the focused control.
    onChange(next.length === 0 ? null : JSON.stringify(next));
  };
  const invalid = (index: number, key: 'weekdays' | 'from' | 'until'): boolean =>
    issues.some(
      (issue) => issue.path[0] === index && (issue.path[1] === key || issue.path.length === 1),
    );

  return (
    <fieldset
      ref={root}
      id={id}
      tabIndex={-1}
      disabled={disabled}
      aria-label={label}
      aria-describedby={describedBy}
      className="min-w-0 space-y-3 rounded-md border border-border p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onFocus={(event) => {
        if (event.target === event.currentTarget) focusFirstInvalid();
      }}
    >
      <legend className="flex items-center gap-1 px-1 text-sm font-medium">
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-0.5 text-destructive">
            *
          </span>
        ) : null}
        <span className="inline-flex [&_button]:h-11 [&_button]:w-11">
          <InlineHelp
            articleId="settings.custom-fields"
            anchor="weekly-times"
            label={t('customFields.types.WEEKLY_SCHEDULE')}
          />
        </span>
      </legend>
      {description === undefined ? null : (
        <p id={`${id}-description`} className="text-xs text-muted-foreground">
          {description}
        </p>
      )}
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {t('customFields.weeklySchedule.hint')}
      </p>
      {windows.map((window, index) => (
        <div key={index} className="space-y-2 rounded-md border border-border p-2">
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-1"
            role="group"
            aria-label={labels.weekdays}
          >
            {DELIVERY_WEEKDAYS.map((day) => (
              <Label
                key={day}
                htmlFor={`${id}-${index}-day-${day}`}
                className="flex min-h-11 min-w-11 items-center justify-center gap-1.5 font-normal"
              >
                <Checkbox
                  id={`${id}-${index}-day-${day}`}
                  checked={window.weekdays.includes(day)}
                  disabled={disabled}
                  aria-invalid={invalid(index, 'weekdays') || undefined}
                  aria-describedby={messages.length > 0 ? `${id}-error` : undefined}
                  onCheckedChange={(checked) =>
                    update(
                      windows.map((entry, row) =>
                        row === index
                          ? {
                              ...entry,
                              weekdays:
                                checked === true
                                  ? [...entry.weekdays, day].sort((a, b) => a - b)
                                  : entry.weekdays.filter((selected) => selected !== day),
                            }
                          : entry,
                      ),
                    )
                  }
                />
                {labels.days[day - 1]}
              </Label>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              onClick={() =>
                update(
                  windows.map((entry, row) =>
                    row === index ? { ...entry, weekdays: DELIVERY_WEEKDAYS.slice(0, 5) } : entry,
                  ),
                )
              }
            >
              {labels.workweek}
            </Button>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            {(['from', 'until'] as const).map((key) => (
              <div key={key} className="min-w-0 flex-1 space-y-1 sm:flex-none">
                <Label htmlFor={`${id}-${index}-${key}`}>{labels[key]}</Label>
                <Input
                  id={`${id}-${index}-${key}`}
                  type="time"
                  className="min-w-28 sm:w-36"
                  value={window[key]}
                  disabled={disabled}
                  aria-invalid={invalid(index, key) || undefined}
                  aria-describedby={messages.length > 0 ? `${id}-error` : undefined}
                  onChange={(event) =>
                    update(
                      windows.map((entry, row) =>
                        row === index ? { ...entry, [key]: event.target.value } : entry,
                      ),
                    )
                  }
                />
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              disabled={disabled}
              onClick={() => update(windows.filter((_, row) => row !== index))}
            >
              {labels.remove}
            </Button>
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-weekly-schedule-add
        disabled={disabled || windows.length >= DELIVERY_WINDOWS_MAX}
        onClick={() => update([...windows, { weekdays: [], from: '', until: '' }])}
      >
        {labels.addWindow}
      </Button>
      {messages.length === 0 ? null : (
        <div id={`${id}-error`} role="alert" className="space-y-1 text-xs text-destructive">
          {messages.map((message) => (
            <p key={message}>{message}</p>
          ))}
        </div>
      )}
      <div className="space-y-1 border-t border-border pt-2">
        <p className="text-xs font-medium text-muted-foreground">{labels.preview}</p>
        <output aria-label={labels.preview} className="block break-words text-sm">
          {validation.success && validation.data.length > 0
            ? formatWeeklySchedule(validation.data, previewLanguage ?? i18n.language)
            : t('customFields.weeklySchedule.empty')}
        </output>
      </div>
    </fieldset>
  );
}
